import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CimaAuthProvider } from './auth/CimaAuthProvider';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { Dashboard } from './pages/Dashboard';
import { ReportIncident } from './pages/ReportIncident';
import { Confirmation } from './pages/Confirmation';
import { Incident } from './types/incident';
import { ChatMessage } from './api/chat';
import { validateInviteToken, acceptInvite } from './api/staff';
import { fetchMe, AuthMe } from './api/auth';
import { StaffProfileProvider } from './hooks/useStaffProfile';
import { ThemeProvider, useTheme } from './hooks/useTheme';
import { setApiUserEmail } from './api/apiClient';
import { IncidentChat } from './pages/IncidentChat';
import { StaffManagement } from './pages/StaffManagement';
import { OnboardPage } from './pages/OnboardPage';
import { TrainingHub } from './pages/training/TrainingHub';
import { CrisisAdvisor } from './pages/CrisisAdvisor';
import { TourProvider } from './tour/TourProvider';
import { WelcomeModal } from './tour/WelcomeModal';
import { TeamsRequiredBanner } from './components/TeamsRequiredBanner';
import { TeamsRequiredModal } from './components/TeamsRequiredModal';
import { useTeamsStatus } from './hooks/useTeamsStatus';
import Login from './pages/Login/Login';
import './index.css';
import './styles/console.css';

type Page = 'dashboard' | 'report' | 'confirmation' | 'chat' | 'settings' | 'training' | 'advisor';

// ── Logo ──────────────────────────────────────────────────────────────────────
function CimaLogo({ size = 36 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <path d="M20 2L36 11V29L20 38L4 29V11L20 2Z" fill="rgba(45,212,191,0.12)" stroke="#2dd4bf" strokeWidth="1.5" />
      <path d="M20 8L30 14V26L20 32L10 26V14L20 8Z" fill="rgba(45,212,191,0.2)" />
      <circle cx="20" cy="18" r="4" fill="#2dd4bf" />
      <circle cx="20" cy="18" r="2" fill="#0d0e14" />
      <path d="M16 18H12M24 18H28M20 14V10M20 22V26" stroke="#2dd4bf" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="18" r="1.5" fill="#2dd4bf" />
      <circle cx="28" cy="18" r="1.5" fill="#2dd4bf" />
      <circle cx="20" cy="10" r="1.5" fill="#2dd4bf" />
      <circle cx="20" cy="26" r="1.5" fill="#2dd4bf" />
    </svg>
  );
}

// ── Auth states ───────────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div className="shell">
      <div className="shell-spinner" />
    </div>
  );
}

function ErrorScreen({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <div className="shell">
      <img src="/crisis-logo.png" alt="Crisis Shield" width={48} height={48} style={{ display: 'block', objectFit: 'contain' }} />
      <p style={{ color: '#f87171', fontWeight: 600, fontSize: 17 }}>Authentication Error</p>
      <p style={{ color: 'rgba(248,250,252,0.45)', fontSize: 13, maxWidth: 340, lineHeight: 1.6 }}>{error.message}</p>
      <button className="submit-btn" style={{ maxWidth: 180, marginTop: 8 }} onClick={retry}>Try again</button>
    </div>
  );
}

// ── Login page ────────────────────────────────────────────────────────────────
function LoginPage() {
  // v2 redesign — the new split-screen Login component. The old inline
  // markup lived here; it's now src/pages/Login/Login.tsx.
  return <Login />;
}

// ── Main authenticated app ────────────────────────────────────────────────────
function CimaApp() {
  const { isLoading, isAuthenticated, loginWithRedirect, error, user } = useAuth0();
  const [page, setPage] = useState<Page>('dashboard');
  const [lastIncident, setLastIncident] = useState<Incident | null>(null);
  const [chatIncident, setChatIncident] = useState<Incident | null>(null);
  const [me, setMe] = useState<AuthMe | null>(null);
  // Lets the Settings dropdown jump straight to a specific tab (e.g. Appearance)
  // instead of always opening on Active Staff.
  const [settingsInitialTab, setSettingsInitialTab] = useState<'staff' | 'invitations' | 'roster' | 'appearance'>('staff');

  // Fetch current user's CIMA role on login + set email for API calls
  React.useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    setApiUserEmail(user.email);
    fetchMe(user.email).then(setMe).catch(() => setMe({ authenticated: false, cimaRole: null }));
  }, [isAuthenticated, user?.email]);

  // Handle invite token after successful Auth0 login
  React.useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite_token');
    if (!token) return;
    // Accept the invite, then clean the URL
    acceptInvite(token, user.email)
      .then(() => {
        console.log('Invite accepted for', user.email);
        window.history.replaceState({}, '', window.location.pathname);
      })
      .catch((err) => console.warn('Invite accept failed (may already be accepted):', err.message));
  }, [isAuthenticated, user?.email]);

  // ── Weekly roster reminder deep-link ──────────────────────────────────
  // The reminder email's "Open Roster" button lands here with
  // ?roster=1&week=YYYY-MM-DD (legacy links use the /roster path). Auth0's
  // redirect_uri is /callback, so the original path/query is wiped on the
  // login round-trip — capture the target into sessionStorage on first
  // paint, before any redirect, then restore it once authenticated.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isRosterLink =
      window.location.pathname.replace(/\/+$/, '') === '/roster' ||
      params.get('roster') === '1';
    if (!isRosterLink) return;
    const week = params.get('week') || '';
    sessionStorage.setItem('cima:roster-deeplink', /^\d{4}-\d{2}-\d{2}$/.test(week) ? week : '');
  }, []);

  // Once logged in (and confirmed manager/admin), restore the deep-link:
  // jump to Settings → Weekly Roster and hand the target week to
  // StaffManagement (which consumes 'cima:roster-week' on mount).
  React.useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    if (me?.cimaRole !== 'ADMIN' && me?.cimaRole !== 'MANAGER') return;
    const week = sessionStorage.getItem('cima:roster-deeplink');
    if (week === null) return;
    sessionStorage.removeItem('cima:roster-deeplink');
    if (week) sessionStorage.setItem('cima:roster-week', week);
    setSettingsInitialTab('roster');
    setPage('settings');
    window.history.replaceState({}, '', '/');
  }, [isAuthenticated, user?.email, me?.cimaRole]);

  // Check for invite_token in URL
  const inviteToken = new URLSearchParams(window.location.search).get('invite_token');

  if (isLoading) return <LoadingScreen />;
  if (error) return <ErrorScreen error={error} retry={() => void loginWithRedirect()} />;

  // If invite_token present and NOT authenticated → show onboard page (collect details first)
  if (inviteToken && !isAuthenticated) {
    return <OnboardPage token={inviteToken} />;
  }

  if (!isAuthenticated) return <LoginPage />;

  // Gate: if user is not in cima_staff (me is null or cimaRole is null), show "not invited" screen
  if (me && !me.cimaRole) {
    return (
      <div className="shell">
        <div style={{ textAlign: 'center', maxWidth: 420, padding: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ color: 'var(--fg)', fontSize: 22, marginBottom: 8 }}>Access Restricted</h2>
          <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
            Your account ({user?.email}) has not been invited to CIMA. Please contact your administrator to get an invitation.
          </p>
          <button
            className="submit-btn"
            style={{ maxWidth: 200, margin: '0 auto' }}
            onClick={() => loginWithRedirect({ authorizationParams: { prompt: 'login' } })}
          >
            Try Different Account
          </button>
        </div>
      </div>
    );
  }

  // Still loading user role
  if (!me) return <LoadingScreen />;

  return (
    <StaffProfileProvider me={me}>
    <TourProvider role={me?.cimaRole as any} currentPage={page as any}>
    <CimaAppInner
      page={page} setPage={setPage}
      settingsInitialTab={settingsInitialTab} setSettingsInitialTab={setSettingsInitialTab}
      lastIncident={lastIncident} setLastIncident={setLastIncident}
      chatIncident={chatIncident} setChatIncident={setChatIncident}
      me={me}
    />
    </TourProvider>
    </StaffProfileProvider>
  );
}

interface InnerProps {
  page: Page; setPage: (p: Page) => void;
  settingsInitialTab: 'staff' | 'invitations' | 'roster' | 'appearance';
  setSettingsInitialTab: (t: 'staff' | 'invitations' | 'roster' | 'appearance') => void;
  lastIncident: Incident | null; setLastIncident: (i: Incident | null) => void;
  chatIncident: Incident | null; setChatIncident: (i: Incident | null) => void;
  me: AuthMe | null;
}

function CimaAppInner({ page, setPage, settingsInitialTab, setSettingsInitialTab,
                        lastIncident, setLastIncident, chatIncident, setChatIncident, me }: InnerProps) {
  const isAdmin = me?.cimaRole === 'ADMIN' || me?.cimaRole === 'MANAGER';
  const teamsStatus = useTeamsStatus();
  const { setTheme } = useTheme();

  // USERs run in LIGHT mode, always (Yash 2026-07-07). They have no Settings
  // access (gated below), so a previously-saved dark preference would trap
  // them in dark with no way back — force light whenever a USER is resolved.
  React.useEffect(() => {
    if (me?.cimaRole === 'USER') setTheme('light');
  }, [me?.cimaRole, setTheme]);

  // If a USER lands on the settings page via any stale path, bounce to the
  // dashboard instead of showing an empty main pane.
  React.useEffect(() => {
    if (page === 'settings' && me && me.cimaRole && !isAdmin) setPage('dashboard');
  }, [page, me, isAdmin, setPage]);

  // Banner only on pages where it's helpful — dashboard, report flow,
  // confirmation. Hide on chat, settings, training (admin is in there
  // setting it up; staff is in read-only mode).
  const showBanner = !teamsStatus.isReady
    && teamsStatus.loaded
    && (page === 'dashboard' || page === 'report' || page === 'confirmation');

  const openIntegrations = () => {
    setSettingsInitialTab('staff'); // dummy; the StaffManagement effect will pick up the deep-link
    setPage('settings');
    // Use a global event to tell StaffManagement to flip to the integrations tab
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('cima:open-settings-tab', { detail: 'integrations' }));
    }, 50);
  };

  // Hard gate — don't even nav to the report page if Teams isn't ready.
  const gatedReport = () => {
    if (!teamsStatus.isReady && teamsStatus.loaded) {
      window.dispatchEvent(new CustomEvent('cima:teams-required'));
      return;
    }
    setPage('report');
  };

  return (
    <div className="cs-shell">
      <WelcomeModal
        role={me?.cimaRole as any}
        displayName={me?.displayName || me?.firstName || null}
        onOpenIntegrations={isAdmin ? openIntegrations : undefined}
      />
      <TeamsRequiredModal isAdmin={isAdmin} onOpenIntegrations={openIntegrations} />

      <Sidebar
        currentPage={page}
        isAdmin={isAdmin}
        onDashboard={() => setPage('dashboard')}
        onReport={gatedReport}
        onAdvisor={() => setPage('advisor')}
        onTraining={() => setPage('training')}
        onStaffRoster={() => { setSettingsInitialTab('staff'); setPage('settings'); }}
        onRoster={() => { setSettingsInitialTab('roster'); setPage('settings'); }}
        onAllIncidents={() => {
          setPage('dashboard');
          setTimeout(() => window.dispatchEvent(new CustomEvent('cima:open-all-incidents')), 60);
        }}
      />

      <div className="cs-body">
        <Topbar
          currentPage={page}
          // Settings is ADMIN/MANAGER-only — USERs get no gear at all (Yash
          // 2026-07-07). Passing undefined hides the button in Topbar.
          onSettings={isAdmin ? () => { setSettingsInitialTab('appearance'); setPage('settings'); } : undefined}
          onNavigate={(target) => {
            if (target === 'dashboard') setPage('dashboard');
            else if (target === 'report') gatedReport();
            else if (target === 'training') setPage('training');
            else if (target === 'advisor') setPage('advisor');
            else if (target === 'settings' && isAdmin) { setSettingsInitialTab('staff'); setPage('settings'); }
          }}
        />
        {showBanner && (
          <TeamsRequiredBanner isAdmin={isAdmin} onOpenIntegrations={openIntegrations} />
        )}
        <main className={`cs-main${page === 'chat' || page === 'advisor' ? ' cs-main-flush' : ''}`}>
        {page === 'dashboard' && (
          <Dashboard
            onReport={() => {
              // Gate the dashboard's Report Incident tile too
              if (!teamsStatus.isReady && teamsStatus.loaded) {
                window.dispatchEvent(new CustomEvent('cima:teams-required'));
                return;
              }
              setPage('report');
            }}
            onOpenChat={(incident) => { setChatIncident(incident); setPage('chat'); }}
            onTraining={() => setPage('training')}
            onAdvisor={() => setPage('advisor')}
            // undefined for USERs so the dashboard hides its settings
            // quick-links entirely (not dead links that no-op on click).
            onSettings={isAdmin ? (tab) => {
              setSettingsInitialTab(tab || 'staff');
              setPage('settings');
            } : undefined}
          />
        )}
        {page === 'advisor' && (
          <CrisisAdvisor onBack={() => setPage('dashboard')} />
        )}
        {page === 'report' && (
          <ReportIncident
            onSuccess={(incident) => {
              setLastIncident(incident);
              setPage('confirmation');
            }}
            onCancel={() => setPage('dashboard')}
          />
        )}
        {page === 'chat' && chatIncident && (
          <IncidentChat
            incident={chatIncident}
            onBack={() => setPage('dashboard')}
            onSitrepGenerated={(incidentId, sitrep) => {
              // Patch the sitrep onto the chatIncident so sidebar stays current
              if (chatIncident && chatIncident.id === incidentId) {
                setChatIncident({ ...chatIncident, sitrep });
              }
            }}
          />
        )}
        {/* Belt-and-braces: USERs can never render the settings page even if
            some path sets page='settings' (deep link, stale state). */}
        {page === 'settings' && isAdmin && (
          <StaffManagement initialTab={settingsInitialTab} />
        )}
        {page === 'training' && (
          <TrainingHub onBack={() => setPage('dashboard')} />
        )}
        {page === 'confirmation' && lastIncident && (
          <Confirmation
            incident={lastIncident}
            onDashboard={() => setPage('dashboard')}
            onReportAnother={() => setPage('report')}
            onStartChat={(lastIncident.severity === 'CRITICAL' || lastIncident.severity === 'MODERATE') ? () => {
              setChatIncident(lastIncident);
              setPage('chat');
            } : undefined}
          />
        )}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <CimaAuthProvider>
        <CimaApp />
      </CimaAuthProvider>
    </ThemeProvider>
  );
}
